import ClientWrapper from "@/ClientWrapper";
import { isMultiTenant, isStaging, showEvents } from "@/Configs/config";
import "@/Main.css";
import { ClientType, Terminal, useTerminalsState } from "@/States/Terminals";
import { translationContext } from "@/States/translationContext";
import { HeaderMWM } from "@/UI/Header";
import { getPermittedTerminals, isCarrotAdmin } from "@/Utils/authUtils";
import { trpc } from "@/Utils/trpc";
import { timezoneStore, useConfig } from "@/api/hooks/useConfig";
import { useSelectedSite } from "@/api/hooks/useSelectedSite";
import { useSites } from "@/api/hooks/useSites";
import ErrorNotice from "@/components/ErrorNotice";
import { SidebarMWM } from "@/components/Sidebar";
import TenantDetails from "@/pages/CustomerDetails/customerDetails";
import { Allocations } from "@/pages/allocations/allocations";
import { Configuration } from "@/pages/configuration";
import TenantDashboard from "@/pages/customer/dashboard";
import ManageCustomers from "@/pages/customer/manage";
import { ImportTenants } from "@/pages/customer/manage/components/import";
import ErrorPage from "@/pages/error/ErrorPage";
import NotFound from "@/pages/error/NotFound";
import Unauthorized from "@/pages/error/Unauthorized";
import { Events } from "@/pages/events/events";
import { GlobalSettings } from "@/pages/globalSettings";
import { Home } from "@/pages/home/home";
import { ImportInfrastructure } from "@/pages/infrastructure/import";
import ManageInfrastructure from "@/pages/infrastructure/manage";
import { Logout } from "@/pages/logout";
import PrintQRCodesPage from "@/pages/qr/PrintQRCodesPage";
import TenantWasteTypes from "@/pages/tenantWasteTypes/tenantWasteTypes";

import React, { ReactNode, useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, Route, Routes, useParams } from "react-router-dom";

import { capitalize } from "lodash";
import { usePostHog } from "posthog-js/react";
import { twMerge } from "tailwind-merge";

import { Loading } from "./App";
import { useAuth0 } from "./Contexts";
import { ModalProvider } from "./Contexts/Modal";
import HeaderRE from "./components/v2/Header";
import SidebarRE from "./components/v2/Sidebar";
import { Contamination } from "./pages/contamination";
import { ImportEvents } from "./pages/events/import";
import { HomeV2 } from "./pages/home_v2";
import { LibraryV2 } from "./pages/library_v2";
import { PortfolioV2 } from "./pages/portfolio_v2";
import { ReportingV2 } from "./pages/reporting_v2";

type Route = {
  path: string;
  element: ReactNode;
  redirects?: string[];
};

const LoadingIndicator = () => {
  return (
    <svg
      className="active"
      viewBox="0 0 100 1"
      preserveAspectRatio="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <line x1="0" y1="0" x2="100" y2="0" />
    </svg>
  );
};

const MWM_ONLY_ROUTES = ["infrastructure/manage/import"];

const EVENTS_ROUTES = ["events", "events/import"];

const RE_ONLY_ROUTES = [
  "portfolio",
  "tenant/dashboard",
  "tenant/details",
  "tenant/waste-streams",
  "settings",
  "reporting",
  "library",
];

const getRoutes = (
  isMWM: boolean,
  config: any,
  isAdmin: boolean,
  hasMultipleTerminals: boolean,
) => {
  return (
    [
      {
        path: "",
        element: isMWM ? <Home /> : <HomeV2 />,
      },
      {
        path: "classic",
        element: <Home />,
      },
      {
        path: "reporting",
        element: <ReportingV2 />,
      },
      {
        path: "events/import",
        element: <ImportEvents />,
      },
      {
        path: "events",
        element: <Events />,
      },
      {
        path: "portfolio",
        element: <PortfolioV2 />,
      },
      {
        path: "contamination",
        element: <Contamination />,
      },
      {
        path: "tenant/dashboard",
        element: <TenantDashboard />,
      },
      {
        path: "tenant/details",
        element: <TenantDetails />,
        redirects: ["/tenant/dashboard/details"],
      },
      {
        path: "tenant/waste-streams",
        element: <TenantWasteTypes />,
        redirects: ["/tenant/waste-types", "/tenant/dashboard/waste-types"],
      },
      {
        path: "customer/manage/import",
        element: <ImportTenants />,
      },
      {
        path: "customer/manage/:id?",
        element: <ManageCustomers />,
      },
      {
        path: "infrastructure/manage/import",
        element: <ImportInfrastructure />,
      },
      {
        path: "infrastructure/manage/:terminalId?/:accessParentId?/:containerId?",
        element: <ManageInfrastructure />,
      },
      {
        path: "settings",
        element: <Configuration />,
      },
      {
        path: "global-settings",
        element: <GlobalSettings />,
      },
      {
        path: "infrastructure/manage/qr-codes",
        element: <PrintQRCodesPage />,
      },
      {
        path: "allocations/:id?",
        element: <Allocations />,
      },
      {
        path: "library",
        element: <LibraryV2 />,
      },
      {
        path: "logout",
        element: <Logout />,
      },
      {
        path: "*",
        element: <NotFound />,
      },
    ].filter(Boolean) as Route[]
  ).filter((route) => {
    if (MWM_ONLY_ROUTES.includes(route.path)) {
      return isMWM;
    }

    if (RE_ONLY_ROUTES.includes(route.path)) {
      return !isMWM;
    }

    if (EVENTS_ROUTES.includes(route.path)) {
      return !isMWM || showEvents;
    }
    if (route.path === "allocations/:id?") {
      return isMWM && config?.useExternalCRM;
    }
    if (route.path === "global-settings") {
      return isAdmin;
    }
    if (route.path === "portfolio") {
      return !isMWM && hasMultipleTerminals;
    }
    // All other routes are available to all
    return true;
  });
};

/**
 *
 * A component that wraps the selected component and provides navigation.
 * This component needs to be within the router to get the client from the url.
 *
 */
const MainLayout = ({
  children,
  error,
}: {
  children: ReactNode;
  error: any;
}) => {
  const { client } = useParams();
  const { selectedSite, setSelectedSite } = useSelectedSite();
  const { isMWM } = useConfig();

  const { sites } = useSites();

  const [collapsedSidebar, setCollapsedSidebar] = useState<boolean>(false);
  const [mobileNavOpen, setMobileNavOpen] = useState<boolean>(false);

  useEffect(() => {
    if (isMWM || !isMultiTenant) {
      setSelectedSite(undefined);
    } else if (client !== selectedSite?.siteName) {
      const foundSite = sites?.find((s) => s.siteName === client);
      if (foundSite) {
        setSelectedSite(foundSite);
      }
    }
  }, [client, selectedSite, setSelectedSite, sites, isMWM]);

  const multiTenantSiteNotFound = useMemo(
    () =>
      isMultiTenant &&
      client &&
      sites &&
      !sites?.find((s) => s.siteName === client),
    [client, sites],
  );

  if (multiTenantSiteNotFound) {
    return <NotFound />;
  }

  return (
    <ModalProvider>
      <div
        id="Main"
        className={twMerge(
          "grid",
          isMWM && "md:grid-cols-main",
          !isMWM &&
            (collapsedSidebar ? "grid-cols-main-full-v2" : "grid-cols-main-v2"),
          !isMWM && "max-[750px]:grid-cols-1",
        )}
      >
        <LoadingIndicator />
        {isMWM ? (
          <SidebarMWM
            showSidebar={!collapsedSidebar}
            toggleMenu={() => setCollapsedSidebar(!collapsedSidebar)}
          />
        ) : (
          <SidebarRE
            collapsedSidebar={collapsedSidebar}
            setCollapsedSidebar={setCollapsedSidebar}
            mobileNavOpen={mobileNavOpen}
            onCloseMobileNav={() => setMobileNavOpen(false)}
          />
        )}
        <main
          className={twMerge(
            "bg-grey1 grid min-h-screen grid-cols-1 transition duration-200 ease-in-out print:min-h-0",
            isMWM && "grid-rows-main",
            !isMWM &&
              (collapsedSidebar
                ? "grid-rows-main-full-v2"
                : "grid-rows-main-v2"),
          )}
        >
          {isMWM ? (
            <HeaderMWM
              toggleMenu={() => setCollapsedSidebar(!collapsedSidebar)}
              sidebarOpen={!collapsedSidebar}
            />
          ) : (
            <HeaderRE
              collapsedSidebar={collapsedSidebar}
              onOpenMobileNav={() => setMobileNavOpen(true)}
            />
          )}
          {!!error && <ErrorNotice />}
          {!error && <div className="row-start-2">{children}</div>}
        </main>
      </div>
    </ModalProvider>
  );
};

const Main: React.FC = () => {
  const {
    isReady: isTerminalStateReady,
    currentTerminal,
    setTerminalState,
    terminals: permittedTerminals,
  } = useTerminalsState();
  const {
    config,
    isConfigLoading,
    isRealEstate,
    isMWM,
    useClientPath,
    isErrorFetchingConfig,
  } = useConfig();
  const { setContext } = translationContext();
  const { timezone } = timezoneStore();
  const isAdmin = isCarrotAdmin();
  const { siteName } = useSelectedSite();
  const { user } = useAuth0()!;
  const posthog = usePostHog();

  useEffect(() => {
    if (!isStaging && user && siteName && (isMWM || currentTerminal?.id)) {
      posthog.identify(user.sub, {
        name: user.name,
        email: user.email,
      });

      posthog.group("client", siteName, {
        name: capitalize(siteName),
      });

      if (currentTerminal?.id) {
        posthog.group("building", currentTerminal.id, {
          name: currentTerminal.name,
        });
      }
    }
  }, [posthog, user, siteName, currentTerminal, isMWM]);

  const {
    data,
    isLoading: loading,
    error,
  } = trpc.terminals.getAll.useQuery(undefined, {
    queryKeyHashFn: () => ["getAllTerminals", siteName].toString(),
    refetchOnWindowFocus: (query) => !query.state.error,
  });

  const terminalId = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const terminalIdFromUrl = urlParams.get("terminal-id");
    const terminalInStorage = localStorage.getItem("terminalId");
    if (terminalIdFromUrl) {
      localStorage.setItem("terminalId", terminalIdFromUrl);
      return terminalIdFromUrl;
    } else if (terminalInStorage) {
      return terminalInStorage;
    }
  }, []);

  useEffect(() => {
    if (isRealEstate && !loading) {
      const { terminals, allTerminals } = getPermittedTerminals(data, isMWM);
      const filteredTerminalIds = terminals.map((t) => t.id);

      let newTerminal = undefined;

      if (
        currentTerminal?.id &&
        filteredTerminalIds.includes(currentTerminal.id)
      ) {
        newTerminal = currentTerminal;
      } else if (terminals?.length === 1) {
        newTerminal = terminals[0];
      } else {
        newTerminal =
          terminals?.find((re: Terminal) => re.id === terminalId) ??
          terminals?.[0] ??
          {};
      }

      setTerminalState({
        type: ClientType.REAL_ESTATE,
        currentTerminal: newTerminal,
        terminals,
        allTerminals,
      });
    } else if (isMWM) {
      const { terminals, allTerminals } = getPermittedTerminals(data, isMWM);
      setTerminalState({
        terminals,
        allTerminals,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (config?.type) {
      setContext(config?.type);
    }
  }, [config?.type, setContext]);

  const hasMultipleTerminals = useMemo(() => {
    return permittedTerminals?.length > 1;
  }, [permittedTerminals]);

  const isError = useMemo(
    () => error || isErrorFetchingConfig,
    [error, isErrorFetchingConfig],
  );

  if (
    !isError &&
    (loading || isConfigLoading || !isTerminalStateReady || !timezone)
  ) {
    console.log("Waiting for terminals/config to load");
    return <Loading />;
  }

  if (error?.message.includes("401")) return <Unauthorized />;

  if (isError || (isRealEstate && !currentTerminal)) {
    return <ErrorPage />;
  }

  return (
    <Routes>
      {/* Support for existing routes. Redirects to routes nested within the selected client */}
      <Route
        element={
          <MainLayout error={error}>
            {useClientPath ? <ClientWrapper /> : <Outlet />}
          </MainLayout>
        }
      >
        {getRoutes(isMWM, config, isAdmin, hasMultipleTerminals).map(
          (route) => (
            <React.Fragment key={route.path}>
              {route.redirects?.map((redirect) => (
                <Route
                  key={redirect}
                  path={redirect}
                  element={<Navigate replace to={`/${route.path}`} />}
                />
              ))}
              <Route
                key={route.path}
                path={route.path}
                element={route.element}
              />
            </React.Fragment>
          ),
        )}
      </Route>

      {/* The routes that are actually used after redirect */}
      {useClientPath && (
        <Route
          path="/:client/*"
          element={
            <MainLayout error={error}>
              <Outlet />
            </MainLayout>
          }
        >
          {getRoutes(isMWM, config, isAdmin, hasMultipleTerminals).map(
            (route) => (
              <Route
                key={route.path}
                path={route.path}
                element={route.element}
              />
            ),
          )}
        </Route>
      )}
    </Routes>
  );
};

export default Main;
